import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn } from "@clerk/clerk-react";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../../../components/ui";
import googleIcon from "../../../assets/svg/auth/google.svg";
import { login, startGoogleLogin } from "../services/authService";
import { useRestoreFromRedirect } from "../hooks/useRestoreFromRedirect";
import { ApiError } from "../../../lib/http";
import { EXTERNAL_LINKS, ROUTES } from "../../../routes/paths";

const LoginPage = () => {
  const navigate = useNavigate();
  // Clerk headless: this screen stays the only UI. `signIn` is the in-flight
  // attempt object, which the OTP screen picks up again from the same hook.
  const { isLoaded, signIn } = useSignIn();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Validation states
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [apiError, setApiError] = useState("");

  // The Google button leaves for Google with `submitting` still set, since
  // nothing after the redirect runs. Coming back must hand the form over.
  useRestoreFromRedirect(() => setSubmitting(false));

  const validateEmail = (value: string): string => {
    if (!value) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setApiError("");
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    // An empty field that was never typed in is not wrong yet, it is just
    // unused. The field is focused on arrival and the Google button sits right
    // below it, so flagging it here would greet anyone taking that route with
    // an error about a form they were never going to fill in. Submitting still
    // asks for it.
    if (!email) {
      setEmailError("");
      return;
    }

    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleSubmit = async () => {
    const emailErr = validateEmail(email);

    setEmailError(emailErr);
    setEmailTouched(true);
    setApiError("");

    if (emailErr || !isLoaded || !signIn) {
      return;
    }

    setSubmitting(true);
    try {
      const { emailAddressId } = await login(signIn, email);
      navigate(ROUTES.verifyOtp, {
        state: { email, emailAddressId, flow: "login" },
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setApiError(error.message);
      } else {
        setApiError(
          "Network error. Please check your connection and try again."
        );
      }
      setSubmitting(false);
      console.error("Log-in error:", error);
    }
  };

  // Leaves for Google's consent screen directly; Clerk comes back to
  // ROUTES.ssoCallback. Nothing after this runs unless the redirect fails.
  //
  // The "login" intent is what stops an unknown Google address from becoming a
  // new account: sign-up is the only route that creates one, because it is the
  // only route that asks for consent first.
  const handleGoogle = async () => {
    if (!isLoaded || !signIn) return;

    setApiError("");
    setSubmitting(true);
    try {
      await startGoogleLogin(signIn, ROUTES.dashboard);
    } catch (error) {
      setApiError(
        error instanceof ApiError
          ? error.message
          : "Could not start Google sign-in. Please try again."
      );
      setSubmitting(false);
      console.error("Google sign-in error:", error);
    }
  };

  const isFormValid = !validateEmail(email) && !submitting && isLoaded;

  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-3.5 bg-white border rounded-lg text-[15px] text-gray-900 placeholder-[#9AA4AE] focus:outline-none transition-all ${
      hasError
        ? "border-red-500"
        : "border-[#DCE0E4] focus:border-[#00C2CB]"
    }`;

  return (
    <AuthLayout>
      <h1 className="text-[24px] leading-tight font-semibold text-[#1F2937]">
        Log in
      </h1>
      <p className="mt-1 text-[14px] text-[#62636C]">
        Please enter your email to continue
      </p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={submitting}
            autoComplete="email"
            autoFocus
            className={inputClasses(!!emailError && emailTouched)}
            placeholder="yourname@email.com"
          />
          {emailError && emailTouched && (
            <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
          )}
        </div>

        {apiError && <p className="text-xs text-red-500">{apiError}</p>}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={!isFormValid}
          className="mt-2"
        >
          Log in
        </Button>

        {/* <p className="text-center text-[13px] leading-relaxed text-[#62636C]">
          By logging in, you agree to our{" "}
          <a
            href={EXTERNAL_LINKS.termsOfUse}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00C2CB] font-medium hover:underline"
          >
            Terms of Use
          </a>
        </p> */}
      </form>

      <div className="my-7 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#E3E7EA]" />
        <span className="text-sm font-medium text-[#9AA4AE]">OR</span>
        <span className="h-px flex-1 bg-[#E3E7EA]" />
      </div>

      <Button
        variant="secondary"
        size="lg"
        fullWidth
        onClick={handleGoogle}
        disabled={submitting || !isLoaded}
        startIcon={<img src={googleIcon} alt="" className="w-5 h-5" />}
      >
        Continue with Google
      </Button>

      <p className="mt-12 text-center text-[14px] text-[#3A4652]">
        Don&rsquo;t have an account?{" "}
        <button
          type="button"
          onClick={() => navigate(ROUTES.signup)}
          className="text-[#00C2CB] font-medium hover:underline"
        >
          Sign up
        </button>
      </p>
    </AuthLayout>
  );
};

export default LoginPage;
